import { type DriveFile } from '@/shared/DriveTypes';
import { CircularProgress, Drawer } from '@mui/material';
import { useMachine } from '@xstate/react';
import * as appStyles from '~/app.css';
import { colors, darkTheme, lightTheme } from '~/theme.css';
import { useAuth, useFile, useFiles, useWikis } from '~/utils/hooks/hooks';
import resizeMachine from '~/utils/machines/resizemachine';
import { useSidebarOpen, useTablet, useTheme } from '~/utils/store';
import { colorIsDark, cx, rem } from '~/utils/utils';
import Tree from '../Tree/Tree';
import WikiSelector from '../WikiSelector/WikiSelector';
import { Resizer } from './Resizer';
import * as styles from './Sidebar.css';
import { defaultWidth, sliderWidth } from './Sidebar.css';

type Props = {
  wikiId: string | undefined;
  id?: string | undefined;
};

export const Sidebar = ({ wikiId, id = wikiId }: Props) => {
  const { data: user } = useAuth();
  const { data: wikis = [], isLoading: wikisLoading } = useWikis();
  const { data: wiki, error: wikiError } = useFile(wikiId, wikis, wikiId);
  const { files, isLoading: filesLoading } = useFiles(wikiId);
  const theme = useTheme();
  const isTablet = useTablet();
  const [sidebarOpen, setSidebarOpen] = useSidebarOpen();
  const { color: bgColor, fontColor } = wiki?.properties ?? {};

  const [resizeState, sendToResize] = useMachine(resizeMachine, {
    input: {
      key: 'sidebar-width',
      defaultWidth,
    },
  });

  const firstWiki = wikis[0];

  if (!wiki && !wikiError) return null;
  if (!wiki && wikiError && !firstWiki) return null;

  const width =
    resizeState.context.width === 0
      ? resizeState.context.defaultWidth + resizeState.context.width
      : resizeState.context.defaultWidth + resizeState.context.width - sliderWidth / 2;

  const contentStyle = {
    ...(theme === 'light' && bgColor && { backgroundColor: bgColor }),
    width: isTablet ? undefined : width,
  };

  const sidebarContent = (
    <div className={styles.sidebarContent} style={contentStyle}>
      <WikiSelector
        wikis={wikis}
        wiki={(wiki ?? firstWiki) as DriveFile}
        user={user}
        isLoading={wikis.length === 0 && wikisLoading}
      />
      {wiki && <Tree files={files} wiki={wiki} wikis={wikis} id={id} closedByDefault showHome />}
      {wiki && filesLoading && files.length === 0 && (
        <div className={cx(appStyles.flex, appStyles.justifyCenter)} style={{ marginTop: rem(18) }}>
          <CircularProgress
            style={{
              color:
                theme === 'dark' && fontColor && colorIsDark(fontColor)
                  ? colors.spinner.sidebar
                  : (fontColor ?? colors.spinner.sidebar),
            }}
            thickness={4}
            size={34}
          />
        </div>
      )}
    </div>
  );

  if (isTablet) {
    return (
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        className={cx({ [darkTheme]: theme === 'dark', [lightTheme]: theme === 'light' })}
      >
        <nav className={styles.sidebarMobile}>{sidebarContent}</nav>
      </Drawer>
    );
  }

  return (
    <nav className={cx(styles.sidebar, appStyles.fadeInSlide300)} style={{ width }}>
      {sidebarContent}
      <Resizer
        positionX={resizeState.context.positionX}
        width={resizeState.context.width}
        send={sendToResize}
        defaultWidth={defaultWidth}
        sliderWidth={sliderWidth}
      />
    </nav>
  );
};
